import { useEffect, useRef, useState } from 'react';
import { logger } from '../../utils/logger';
import { useHistory, useLocation } from 'react-router-dom';
import { IonPage, IonContent, IonSpinner, IonButton, IonText } from '@ionic/react';
import { supabase } from '../../services/supabase';
import { App } from '@capacitor/app';
import { Capacitor } from '@capacitor/core';

const AuthCallback: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const hasProcessed = useRef(false);
  const [verificationState, setVerificationState] = useState<'processing' | 'success' | 'error'>('processing');

  useEffect(() => {
    // Prevent multiple executions
    if (hasProcessed.current) {
      return;
    }

    const handleCallback = async () => {
      try {
        hasProcessed.current = true;

        logger.log('🔧 Auth callback triggered');
        logger.log('📍 Location:', location.pathname);
        logger.log('🔑 Hash:', location.hash);

        // For mobile deep links, the hash contains the tokens
        if (location.hash) {
          const hashParams = new URLSearchParams(
            location.hash.substring(1) // Remove the # at the start
          );

          const accessToken = hashParams.get('access_token');
          const refreshToken = hashParams.get('refresh_token');
          const type = hashParams.get('type');

          logger.log('🎫 Token type:', type);
          logger.log('✅ Access token found:', !!accessToken);

          // CRITICAL: For password recovery on native, redirect to reset password in-app
          // For web, recovery goes directly to /reset-password.html (standalone page)
          if (type === 'recovery' && accessToken && Capacitor.isNativePlatform()) {
            logger.log('🔐 Native password recovery flow - storing token and redirecting');
            sessionStorage.setItem('dicecat_recovery_token', accessToken);
            if (refreshToken) {
              sessionStorage.setItem('dicecat_recovery_refresh_token', refreshToken);
            }
            history.replace('/auth/reset-password');
            return;
          }

          // If it's a web recovery flow, this shouldn't happen (should go to standalone page)
          // But if it does, redirect to login
          if (type === 'recovery' && !Capacitor.isNativePlatform()) {
            logger.log('⚠️ Web recovery flow detected in app - redirecting to login');
            history.replace('/login');
            return;
          }

          if (accessToken) {
            // Set the session manually from the tokens (NOT for recovery)
            logger.log('🔐 Setting session from tokens...');
            const { data, error } = await supabase.auth.setSession({
              access_token: accessToken,
              refresh_token: refreshToken || '',
            });

            if (error) {
              logger.error('❌ Error setting session:', error);
              setVerificationState('error');
              return;
            }

            if (data.session) {
              logger.log('✅ Session established successfully!');
              logger.log('👤 User:', data.session.user.email);

              // 🆕 FIX: Force app restart on mobile to refresh profile context
              if (Capacitor.isNativePlatform()) {
                logger.log('🔄 Native platform detected - forcing app restart...');

                // Set a flag in localStorage so we know to redirect after restart
                localStorage.setItem('dicecat_post_verification', 'true');

                // Small delay to ensure session is fully saved
                await new Promise(resolve => setTimeout(resolve, 500));

                // Force app to exit and restart
                try {
                  await App.exitApp();
                } catch (appError) {
                  logger.log('⚠️ App.exitApp not available, using fallback');
                  // Fallback: redirect and force reload
                  window.location.href = '/tabs/discover';
                  window.location.reload();
                }
              } else {
                // Web platform - show success message with deep link option
                logger.log('🌐 Web platform detected - showing verification success');
                setVerificationState('success');
              }
            } else {
              logger.error('❌ No session created');
              setVerificationState('error');
            }
          } else {
            logger.log('⚠️ No access token in hash, checking existing session...');
            // Fallback: check if there's already a session
            const { data } = await supabase.auth.getSession();
            if (data.session) {
              logger.log('✅ Existing session found, redirecting...');
              history.replace('/tabs/discover');
            } else {
              logger.log('❌ No session found, redirecting to login');
              history.replace('/login');
            }
          }
        } else {
          logger.log('⚠️ No hash parameters, checking existing session...');
          // No hash, check if there's already a session
          const { data } = await supabase.auth.getSession();
          if (data.session) {
            logger.log('✅ Existing session found, redirecting...');
            history.replace('/tabs/discover');
          } else {
            logger.log('❌ No session found, redirecting to login');
            history.replace('/login');
          }
        }
      } catch (err) {
        logger.error('💥 Callback error:', err);
        setVerificationState('error');
      }
    };

    handleCallback();
  }, []); // Empty dependency array - only run once on mount

  const handleOpenApp = () => {
    // Attempt to open the app via deep link
    window.location.href = 'com.dicecat.app://tabs/discover';

    // Fallback after 2 seconds if app didn't open
    setTimeout(() => {
      logger.log('App did not open - user may not have it installed');
    }, 2000);
  };

  return (
    <IonPage>
      <IonContent className="ion-padding">
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          height: '100%',
          textAlign: 'center',
          padding: '20px'
        }}>
          {verificationState === 'processing' && (
            <>
              <IonSpinner />
              <p style={{ marginTop: '20px' }}>Verifying your email...</p>
              <p style={{ fontSize: '12px', color: 'var(--ion-color-medium)', marginTop: '10px' }}>
                This will only take a moment
              </p>
            </>
          )}

          {verificationState === 'success' && (
            <>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>✅</div>
              <h2 style={{ color: 'var(--ion-color-success)' }}>Email Verified!</h2>
              <p style={{ marginTop: '10px', color: 'var(--ion-color-medium)' }}>
                Your email has been successfully verified.
              </p>
              <IonButton
                expand="block"
                style={{ marginTop: '30px', maxWidth: '300px' }}
                onClick={handleOpenApp}
              >
                Open Dice Cat App
              </IonButton>
              <p style={{ fontSize: '12px', color: 'var(--ion-color-medium)', marginTop: '20px' }}>
                Don't have the app? Download it from your app store.
              </p>
            </>
          )}

          {verificationState === 'error' && (
            <>
              <div style={{ fontSize: '64px', marginBottom: '20px' }}>❌</div>
              <h2 style={{ color: 'var(--ion-color-danger)' }}>Verification Failed</h2>
              <p style={{ marginTop: '10px', color: 'var(--ion-color-medium)' }}>
                We couldn't verify your email. Please try again or contact support.
              </p>
              <IonButton
                expand="block"
                style={{ marginTop: '30px', maxWidth: '300px' }}
                routerLink="/login"
              >
                Go to Login
              </IonButton>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AuthCallback;